import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import {
  UserActions,
  UserSettingsActions,
} from '../../data-access/state/user-account-settings.actions';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserAccountSettingsSelectors } from '../../data-access/state/user-account-settings.selector-types';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Actions, ofType } from '@ngrx/effects';
import { AsyncPipe, NgIf } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { EnableEscCloseDirective, UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-verify-credentials',
  standalone: true,
  imports: [
    MatDialogClose,
    ReactiveFormsModule,
    PushPipe,
    NgIf,
    AsyncPipe,
    LetDirective,
    TranslatePipe,
    UiButtonComponent,
  ],
  templateUrl: './verify-email-modal.component.html',
  styleUrl: './verify-email-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class VerifyEmailModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<VerifyEmailModalComponent>);
  private store$ = inject(Store);
  private actions$ = inject(Actions);
  private dialogData = inject<{ onEmailConfirmed: () => void }>(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  public selectIsSaving$: Observable<boolean> = this.store$.pipe(
    select(UserAccountSettingsSelectors.selectIsSaving),
  );

  public emailControl = new FormControl<string>('', [
    Validators.required.bind(Validators),
    Validators.email.bind(Validators),
  ]);
  public confirmationCodeControl = new FormControl<string>('', [
    Validators.required.bind(Validators),
    Validators.pattern('^[0-9]*$'),
    Validators.minLength(6),
    Validators.maxLength(6),
  ]);
  public readonly countDown = 60;

  public codeSentId$ = new BehaviorSubject<string | null>(null);
  public showSuccess$ = new BehaviorSubject<boolean>(false);
  public time$ = new BehaviorSubject<number>(3);
  public resendTimer$ = new BehaviorSubject<number>(this.countDown);

  public interval: ReturnType<typeof setInterval> | null = null;

  public ngOnInit(): void {
    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(UserActions.changeEmailRequestSuccess))
      .subscribe((res) => {
        this.codeSentId$.next(res.id);
        this.startTimer();
      });

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(UserSettingsActions.confirmCodeSuccess),
      )
      .subscribe(() => {
        this.showSuccess$.next(true);
        this.dialogData.onEmailConfirmed();
        this.interval = setInterval(() => {
          const currentTime = this.time$.getValue();
          if (!currentTime && this.interval !== null) {
            clearInterval(this.interval);
          } else {
            this.time$.next(currentTime - 1);
          }
        }, 1000);
      });
  }

  public close(): void {
    this.dialogRef.close();
  }

  public sendCode(): void {
    const email = this.emailControl.value ?? '';
    this.store$.dispatch(UserActions.startChangeEmailProcess({ email }));
  }

  public confirmCode(): void {
    const codeValue = this.confirmationCodeControl.value ?? '0';
    const verificationId = this.codeSentId$.getValue();
    if (!verificationId) {
      return;
    }
    this.store$.dispatch(
      UserSettingsActions.startConfirmCode({
        code: Number(codeValue),
        id: verificationId,
      }),
    );
  }

  public startTimer(): void {
    let totalTime = this.countDown;
    const timerInterval = setInterval(() => {
      totalTime = totalTime - 1;
      if (!totalTime) {
        clearInterval(timerInterval);
      }
      this.resendTimer$.next(totalTime);
    }, 1000);
  }
}
